import { Component, Input, OnInit } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize } from "rxjs/operators";
import { IUnitLog, UnitLogService } from "./unit-log.service";
import "./unit-logs.component.scss";

@UntilDestroy()
@Component({
    selector: "unit-logs",
    template: require("./unit-logs.component.html")
})
export class UnitLogsComponent implements OnInit {

    @Input() serial: string;

    loading = false;
    logs: IUnitLog[] = [];
    selectedLog: IUnitLog;

    defaultDateRange = DateRange.Last7Days;

    tableColumns: IVTableColumn<IUnitLog>[];
    tableRows: IVTableRow<IUnitLog>[] = [];

    filterEvent: IAggregateFilterEvent<IUnitLog[]>;

    constructor(
        private readonly unitLogService: UnitLogService,
        private readonly vTableService: VTableService) { }

    ngOnInit() {
        this.createTable();
    }

    onRowClicked(row: IVTableRow<IUnitLog>) {
        this.selectedLog = row.data;
    }

    onFilterChanged(event: IAggregateFilterEvent<IUnitLog[]>) {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.getLogs();
            return;
        }
    }

    // ---

    private getLogs() {
        this.loading = true;

        this.unitLogService.getLogs({ serial: this.serial, start: this.filterEvent.date[0], end: this.filterEvent.date[1] })
            .pipe(
                untilDestroyed(this),
                finalize(() => this.loading = false))
            .subscribe(logs => {
                this.logs = logs;
                this.tableRows = this.vTableService.createTableRows(this.logs, this.tableColumns);
            });
    }

    private createTable(): void {

        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: false }),
                createCellFunction: (log) => this.vTableService.createStatusCell(log.Errored ? VTableRowStatus.Error : VTableRowStatus.Info)
            },
            {
                header: this.vTableService.createDateTableHeader({ sorted: true, tKey: "Common.Time", sortDirection: SortDirection.Descending }),
                createCellFunction: (log) => this.vTableService.createDateCell(new Date(log.Timestamp), {dateTime: true})
            },
            {
                header: this.vTableService.createTableHeader({ title: "Processing Time", width: 140 }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.ProcessingTime + "ms")
            },
            {
                header: this.vTableService.createTableHeader({ title: "Event Type", width: 120 }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.EventType)
            },
            {
                header: this.vTableService.createTableHeader({ title: "Host", width: 150 }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.HostDescription)
            },
            {
                header: this.vTableService.createTableHeader({ title: "Raw" }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.Raw)
            },
            {
                header: this.vTableService.createTableHeader({ title: "Error" }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.ErrorMessage)
            }
        ];
    }
}
